import { formatPriceNew } from "src/utils/formatPrice";
import { _does } from "src/components/Planner/planner/uMath";
import { ts } from "src/utils/translate";

import { clearStringOfQuoteIDs } from "src/utils/controllerAdapterForOfferPage";
import { ProjectQuoteUserProperties } from 'src/model/api/project.model';
import { IconWorkMinutes } from 'src/model/api/quote/get-quote-response.model';

export const getBomColumns = (columns: any) => {
  const bomColumns = [...columns];
  const userManufacturerNameIndex = bomColumns.findIndex(
    (column) => column.data === "user_manufacturer_name"
  );
  if (userManufacturerNameIndex !== -1) {
    bomColumns.splice(userManufacturerNameIndex, 1);
  }

  bomColumns.push({
    data: "total_price",
    title: ts('quote_total_price', 'quoteTotalPrice'),
    responsivePriority: 1,
  });

  bomColumns.forEach((column) => {
    column.name = column.data;
    if (column.data.indexOf("price_") == 0) {
      column.className = "all numeric-cell";
      column.width = "150px";
    } else if (
      column.data == "manufacturer_name" ||
      column.data == "color_name" ||
      column.data == "custom_description"
    ) {
      column.visible = false;
    } else if (column.data == "total_price") {
      column.width = "150px";
      column.className = "numeric-cell";
    } else if (column.data == "quantity") {
      column.responsivePriority = 1;
      column.width = "60px";
      column.className = "dt-qty numeric-cell";
    } else if (column.data == "custom_description") {
      column.responsivePriority = 1;
      column.className = "custdesc-cell";
    } else if (column.data == "article_number") {
      column.visible = false;
    } else if (column.data == "category_name") {
      column.width = "150px";
    }
  });
  return bomColumns;
};

const isEnabled = (
    entitytype: string, quoteUserProperties: ProjectQuoteUserProperties) => {
  switch (entitytype) {
    case "convfunctiontable":
      return quoteUserProperties.include_conv_dev_in_ec === 1;
    case "nhcdintable":
      return quoteUserProperties.include_nhc_din_table === 1;
    case "projectcablestable":
      return quoteUserProperties.include_cables === 1;
    case "racktable":
      return quoteUserProperties.include_it_products === 1;
    case "functiontable":
      return quoteUserProperties.include_functions_table === 1;
    case "projectcustomtable":
      return false;
    default:
      return true;
  }
};

const filterTableData = (
    tableData: any, quoteUserProperties: ProjectQuoteUserProperties) => {
  const filteredTableData: any[] = [];

  tableData.map((data: any) => {
    const tableWithBackBox =
      data.entitytype === "roomtable" &&
      data.rows.find((row: any) => row.is_back_box);

    if (quoteUserProperties.include_back_boxes === 0 && tableWithBackBox) {
      const filteredData = Object.assign({}, data);
      filteredData.rows = filteredData.rows.filter(
        (row: any) => row.is_back_box !== true
      );
      filteredTableData.push(filteredData);
    } else if (isEnabled(data.entitytype, quoteUserProperties)) {
      filteredTableData.push(data);
    }
  });

  return filteredTableData;
};

const setBomRows = (dataSource: any, dataTarget: any) => {
  if (dataSource && Array.isArray(dataSource)) {
    dataSource.forEach((entity) => {
      if (entity.hasOwnProperty("rows")) {
        entity.rows.forEach((row: any) => {
          dataTarget.push(row);
        });
      }
    });
  }
};

export const getBomRows = (
    tableData: any, quoteUserProperties: ProjectQuoteUserProperties) => {
  const bomRows = { devices: [], services: [] };

  //tables filter
  if (quoteUserProperties.include_tables === 0) {
    tableData = tableData.filter((table: any) => table.entitytype !== "roomtable");
  }

  //backbox filter
  let filteredDevices = filterTableData(tableData, quoteUserProperties);
  setBomRows(filteredDevices, bomRows.devices);

  //custom section item filter
  if (quoteUserProperties.include_custom_section === 0) {
    bomRows.devices = bomRows.devices.filter((device: any) => !device.custom_section_item_id);
  }

  //labour cost filter
  if (quoteUserProperties.include_labour_cost === 1) {
    setBomRows(
      tableData.filter((data: any) => data.entitytype === "projectcustomtable"),
      bomRows.services
    );
  }

  return bomRows;
};

export const getNewBomColumns = (
  columns: any,
  currencyVisibility: number,
  currency: string,
  quoteUserProperties: ProjectQuoteUserProperties,
) => {
  const currencyKey = `price_${currency}`;

  let result = columns.filter(
    (col: any) =>
      typeof col.name === "string" &&
      _does([
        "product_name",
        "article_number",
        "quantity",
        `price_${currency}`,
        "total_price",
      ]).contain(col.name)
  );

  if (+currencyVisibility < 2) {
    result = result.filter((col: any) => col.name.indexOf("price") === -1);
  }

  if (quoteUserProperties.include_article_number === 0) {
    result = result.filter((col: any) => col.name.indexOf("article_number") === -1);
  }

  result.forEach((col: any) => {
    // default case
    col.render = (self: any, full: any) => {
      return self || "";
    };

    // unique cases
    // current currency
    if (col.data === currencyKey) {
      col.width = 150;
      col.render = (self: any, full: any) => {
        return formatPriceNew(self, currency);
      };
      col.numeric = true;
    } else if (col.data === "total_price") {
      col.width = 150;
      col.render = (self: any, full: any) => {
        return formatPriceNew(full[currencyKey] * full.quantity, currency);
      };
      col.numeric = true;
    } else if (col.data === "product_name") {
      col.render = (self: any, full: any) => {
        return clearStringOfQuoteIDs(self);
      };
    } else if (col.data === "quantity") {
      col.width = 35;
      col.numeric = true;
    }
  });

  let fromIndex = result.findIndex((col: any) => col.data === "article_number");
  const toIndex = 1;
  if (fromIndex > 0) {
    result.splice(toIndex, 0, result.splice(fromIndex, 1)[0]);
  }

  return result;
};

export const calculateLabourCost = (
    bomRows: any, currency: string, workHourPrices: any, iconWorkMinutes: IconWorkMinutes) => {
  const { devices, services } = bomRows;

  const labourCost = {
    installation: {
      custom: false,
      minutes: 0,
      hours: 0,
      hourlyRate: 0,
      total: 0,
    },
    personalisation: {
      custom: false,
      minutes: 0,
      hours: 0,
      hourlyRate: 0,
      total: 0,
    },
  }

  if(services && Array.isArray(services) && services.length > 0) {
    const installationRow = services.find((service) => service.specification_type === "installation");
    const personalisationRow = services.find((service) => service.specification_type === "personalisation");

    //count all working minutes
    if(devices && Array.isArray(devices) && devices.length > 0) {
      devices.map((device) => {
        if(installationRow && device.installation_minute) {
          if (device.original_length) {
            labourCost.installation.minutes += device.installation_minute * device.original_length; // for cables
          } else if (device.quantity) {
            labourCost.installation.minutes += device.quantity * device.installation_minute;
          }
        }

        if(personalisationRow && device.personalisation_minute) {
          if (device.original_length) {
            labourCost.personalisation.minutes += device.personalisation_minute * device.original_length; // for cables
          } else if (device.quantity) {
            labourCost.personalisation.minutes += device.quantity * device.personalisation_minute;
          }
        }
      })
    }

    if (installationRow) {
      labourCost.installation.minutes += iconWorkMinutes.installation_minute;
    }
    if (personalisationRow) {
      labourCost.personalisation.minutes += iconWorkMinutes.personalisation_minute;
    }

    //Set hours
    labourCost.installation.hours = Math.ceil(labourCost.installation.minutes/60);
    labourCost.personalisation.hours = Math.ceil(labourCost.personalisation.minutes/60);

    //Set prices
    if(workHourPrices) {
      if(installationRow) {
        if(!installationRow.edited_buy_user) {
          labourCost.installation.hourlyRate = workHourPrices.installation[currency];
          labourCost.installation.total = labourCost.installation.hours * workHourPrices.installation[currency];
        } else {
          labourCost.installation.custom = true;
          labourCost.installation.hourlyRate = installationRow[`price_${currency}`] ? installationRow[`price_${currency}`] : 0;
          labourCost.installation.total = labourCost.installation.hours * labourCost.installation.hourlyRate;
        }
      }

      if(personalisationRow) {
        if(typeof personalisationRow !== "undefined" && !personalisationRow.edited_buy_user) {
          labourCost.personalisation.hourlyRate = workHourPrices.personalisation[currency];
          labourCost.personalisation.total = labourCost.personalisation.hours * workHourPrices.personalisation[currency];
        } else {
          labourCost.personalisation.custom = true;
          labourCost.personalisation.hourlyRate = personalisationRow[`price_${currency}`] ? personalisationRow[`price_${currency}`] : 0;
          labourCost.personalisation.total = labourCost.personalisation.hours * labourCost.personalisation.hourlyRate;
        }
      }
    }
  }

  return labourCost;
}
